/**
 * home-searchbar.js
 * Cascading vehicle dropdown powered by Typesense facet queries.
 * Replaces the previous 2.6MB hardcoded JS data object.
 *
 * Flow:
 *   Page load          → fetch all manufacturers (facet, no filter)
 *   Manufacturer pick  → fetch years for that manufacturer
 *   Year pick          → fetch model types for manufacturer + year
 *   Model type pick    → fetch model lines for manufacturer + year + model type
 *   Model line pick    → redirect to /pages/battery-finder with URL params
 */

document.addEventListener('DOMContentLoaded', () => {
  const form = document.getElementById('ymm-filter');
  if (!form) return;

  // TODO: remove hardcoded fallbacks once theme settings are confirmed working
  const host = form.dataset.typesenseHost || 'https://typesense-production-d960.up.railway.app';
  const key  = form.dataset.typesenseKey  || 'ts_8f9e2c1d4a7b6e5f3c2d1a9b8c7e6f5d';

  if (!host || !key) {
    console.warn('Battery finder: Typesense host or key not configured in theme settings.');
    return;
  }

  const manufacturerDropdown = document.getElementById('manufacturer');
  const yearDropdown         = document.getElementById('year');
  const modelDropdown        = document.getElementById('model');
  const lineDropdown         = document.getElementById('line');
  const errorMsg             = document.getElementById('home-searchbar-error');

  // ─── Typesense facet query ───────────────────────────────────────────────

  async function fetchFacet(facetField, filters = {}) {
    const filterParts = Object.entries(filters)
      .map(([field, value]) => `${field}:=${encodeURIComponent(value)}`)
      .join(' && ');

    const params = new URLSearchParams({
      q:                  '*',
      query_by:           'battery_name',  // must be a string field; facet_by handles the actual field
      facet_by:           facetField,
      per_page:           '0',
      max_facet_values:   '1000',
    });

    if (filterParts) {
      params.set('filter_by', Object.entries(filters)
        .map(([field, value]) => `${field}:=[${value}]`)
        .join(' && '));
    }

    const response = await fetch(
      `${host}/collections/batteries/documents/search?${params.toString()}`,
      { headers: { 'X-TYPESENSE-API-KEY': key } }
    );

    if (!response.ok) {
      throw new Error(`Typesense error: ${response.status}`);
    }

    const data = await response.json();
    const counts = data.facet_counts?.[0]?.counts ?? [];

    // Return values sorted alphabetically
    return counts
      .map(c => c.value)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  // ─── Dropdown helpers ────────────────────────────────────────────────────

  function populateDropdown(dropdown, values, placeholder) {
    dropdown.innerHTML = `<option value="">${placeholder}</option>`;
    values.forEach(value => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      dropdown.appendChild(option);
    });
  }

  function resetDropdown(dropdown, placeholder) {
    dropdown.innerHTML = `<option value="">${placeholder}</option>`;
    dropdown.disabled = true;
  }

  function showError(show) {
    if (show) {
      errorMsg.removeAttribute('hidden');
    } else {
      errorMsg.setAttribute('hidden', 'hidden');
    }
  }

  // ─── Initialise: load manufacturers on page load ─────────────────────────

  async function init() {
    try {
      const manufacturers = await fetchFacet('manufacturer');
      populateDropdown(manufacturerDropdown, manufacturers, 'Select Vehicle Manufacturer');
    } catch (err) {
      console.error('Battery finder: failed to load manufacturers.', err);
    }
  }

  // ─── Event listeners ─────────────────────────────────────────────────────

  manufacturerDropdown.addEventListener('change', async () => {
    const manufacturer = manufacturerDropdown.value;

    resetDropdown(yearDropdown,  'Select Year');
    resetDropdown(modelDropdown, 'Select Vehicle Model Type');
    resetDropdown(lineDropdown,  'Select Vehicle Model Line');
    showError(false);

    if (!manufacturer) return;

    try {
      const years = await fetchFacet('year', { manufacturer });
      // Sort years newest first for the dropdown
      const sortedYears = years.sort((a, b) => Number(b) - Number(a));
      populateDropdown(yearDropdown, sortedYears, 'Select Year');
      yearDropdown.disabled = false;
    } catch (err) {
      console.error('Battery finder: failed to load years.', err);
    }
  });

  yearDropdown.addEventListener('change', async () => {
    const manufacturer = manufacturerDropdown.value;
    const year         = yearDropdown.value;

    resetDropdown(modelDropdown, 'Select Vehicle Model Type');
    resetDropdown(lineDropdown,  'Select Vehicle Model Line');
    showError(false);

    if (!year) return;

    try {
      const modelTypes = await fetchFacet('model_type', { manufacturer, year });
      populateDropdown(modelDropdown, modelTypes, 'Select Vehicle Model Type');
      modelDropdown.disabled = false;
    } catch (err) {
      console.error('Battery finder: failed to load model types.', err);
    }
  });

  modelDropdown.addEventListener('change', async () => {
    const manufacturer = manufacturerDropdown.value;
    const year         = yearDropdown.value;
    const model_type   = modelDropdown.value;

    resetDropdown(lineDropdown, 'Select Vehicle Model Line');
    showError(false);

    if (!model_type) return;

    try {
      const modelLines = await fetchFacet('model_line', { manufacturer, year, model_type });
      populateDropdown(lineDropdown, modelLines, 'Select Vehicle Model Line');
      lineDropdown.disabled = false;
    } catch (err) {
      console.error('Battery finder: failed to load model lines.', err);
    }
  });

  lineDropdown.addEventListener('change', () => {
    const manufacturer = manufacturerDropdown.value;
    const year         = yearDropdown.value;
    const model_type   = modelDropdown.value;
    const model_line   = lineDropdown.value;

    if (!manufacturer || !year || !model_type || !model_line) {
      showError(true);
      return;
    }

    showError(false);

    const params = new URLSearchParams({ manufacturer, year, model_type, model_line });
    window.location.href = `/pages/battery-finder?${params.toString()}`;
  });

  // ─── Start ───────────────────────────────────────────────────────────────

  init();
});
